import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Container, Row, Accordion } from 'react-bootstrap';
import AdminNav from '../../components/adminnav/AdminNav';
import { apiCall } from "../../helpers/api";
import AlertModal from "../../components/alerts/alert";
import Select from "react-select";
import { getCountries } from "../../helpers/dropdowns";

import FullPageSpinner from "../../components/spinner/FullPageSpinner";
import Topbar from "../../components/topbar/Topbar";
import { getCurrencies } from "../../helpers/dropdowns";

export default function AdminSettings() {  

  const [settingsData, setSettingsData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAlert, setAlert] = useState({"display":"toast","visible":false,"title":"","content":""});
  const countryOptions = useMemo(() => getCountries(), []);

  function getFieldSchema(setting, fieldName) {
    if (!setting || !setting.fieldSchema) return {};
    return setting.fieldSchema[fieldName] || {};
  }

  function getFieldType(setting, fieldName, value) {
    const schema = getFieldSchema(setting, fieldName);
    if (schema.type) {
      return schema.type;
    }

    if (Array.isArray(setting?.sensitiveFields) && setting.sensitiveFields.includes(fieldName)) {
      return "secret";
    }
    if (typeof value === "boolean") return "toggle";
    if (typeof value === "number") return "number";
    if (typeof value === "string") return "text";
    return "json";
  }

  function getOptionsBySource(source) {
    if (source === "countries") {
      return countryOptions;
    }
    return [];
  }

  function toSelectValue(rawValue, options) {
    const values = Array.isArray(rawValue) ? rawValue : [];
    return values.map((item) => {
      const foundOption = options.find((option) => option.value === item);
      return foundOption || { value: item, label: item };
    });
  }

  const getSettings = useCallback(async () => {
    var response = await apiCall('admin/settings',{ "method" : "GET" });
    if (response["success"]){
        setSettingsData(response["success"]["settings"])
        setIsLoaded(true)
    } 
    setIsLoaded(true)
  }, []);

  useEffect(() => {
    getSettings();
  }, [getSettings]);

  function getCurrencyOptions() {
    return getCurrencies().map((currency) => ({
      value: currency.code,
      label: `${currency.code} - ${currency.name}`,
      currency
    }));
  }

  async function submit(event) {
    event.preventDefault();
    const updatedSettings = settingsData
      .filter((setting) => setting.updated)
      .map(({ updated, ...rest }) => rest);
    // Validate data
    var valid = true;
    if (valid){
        var response = await apiCall('admin/settings',{ "method" : "PUT", "data" : updatedSettings });
        if (response["success"]){
            getSettings();
            setAlert({"display":"toast","visible":true,"title":"Settings Saved","content":"Settings have been updated","className":"bg-success text-white"})
        }
        else {
          setAlert({"display":"toast","visible":true,"title":"Failed to Save Settings","content":response["failure"]["error"],"className":"bg-danger text-white"})
        }
    }
  }

  function updateSettingsData(props, targetName, targetKey) {
    const { type, checked, value } = props.target;
    let nextValue = type === "checkbox" ? checked : value;

    if (type === "number") {
      nextValue = value === "" ? null : Number(value);
      if (value !== "" && Number.isNaN(nextValue)) {
        return;
      }
    }

    updateSettingsValue(targetName, targetKey, nextValue);
  }

  function updateSettingsValue(targetName, targetKey, value) {
    setSettingsData((prev) =>
      prev.map((setting) =>
        setting.name === targetName
          ? {
              ...setting,
              settings: {
                ...setting.settings,
                [targetKey]: value,
              },
              updated: true,
            }
          : setting
      )
    );
  }

  function renderSettingInput(setting, settingName) {
    const value = setting.settings[settingName];
    const fieldSchema = getFieldSchema(setting, settingName);
    const fieldType = getFieldType(setting, settingName, value);
    const inputId = `${setting.name}-${settingName}`;

    if (fieldType === "currency-select") {
      return (
        <Select
          inputId={inputId}
          isMulti
          isDisabled={setting.readOnly}
          options={getCurrencyOptions()}
          value={(value || []).map((currency) => ({
            value: currency.code,
            label: `${currency.code} - ${currency.name}`,
            currency,
          }))}
          onChange={(selected) =>
            updateSettingsValue(setting.name, settingName, (selected || []).map((item) => item.currency))
          }
        />
      );
    }

    if (fieldType === "toggle") {
      return (
        <input
          type="checkbox"
          disabled={setting.readOnly}
          className="form-check-input"
          id={inputId}
          checked={!!value}
          onChange={e => updateSettingsData(e, setting.name, settingName)}
        />
      );
    }

    if (fieldType === "number") {
      return (
        <input
          type="number"
          disabled={setting.readOnly}
          className="form-control"
          id={inputId}
          value={value ?? ""}
          onChange={e => updateSettingsData(e, setting.name, settingName)}
        />
      );
    }

    if (fieldType === "text" || fieldType === "secret") {
      return (
        <input
          type={fieldType === "secret" ? "password" : "text"}
          disabled={setting.readOnly}
          className="form-control"
          id={inputId}
          value={value ?? ""}
          onChange={e => updateSettingsData(e, setting.name, settingName)}
        />
      );
    }

    if (fieldType === "multi-select") {
      const options = getOptionsBySource(fieldSchema.optionsSource);
      return (
        <Select
          inputId={inputId}
          isMulti
          isDisabled={setting.readOnly}
          options={options}
          value={toSelectValue(value, options)}
          onChange={(selected) =>
            updateSettingsValue(
              setting.name,
              settingName,
              (selected || []).map((item) => item.value)
            )
          }
        />
      );
    }

    return (
      <textarea
        type="text"
        rows={5}
        cols={50}
        disabled={setting.readOnly}
        className="form-control"
        id={inputId}
        value={value === undefined || value === null ? "" : JSON.stringify(value, null, 4)}
        onChange={e => updateSettingsData(e, setting.name, settingName)}
      />
    );
  }

  function renderSettingSection(scope) {
    return settingsData.filter(function(setting) {return setting.scope === scope}).map((setting) => (
      <div key={setting.name} className="border-bottom mb-4">
        <h5 className="text-capitalize">{setting.name}</h5>
        <div className="mt-3">
          {Object.keys(setting.settings).map((settingName) => {
            const fieldSchema = getFieldSchema(setting, settingName);
            const inputId = `${setting.name}-${settingName}`;
            return (
              <div className="form-group mb-3" key={setting.name+"-"+settingName}>
                <label className="text-capitalize" htmlFor={inputId}>{fieldSchema.label || settingName}</label>
                {renderSettingInput(setting, settingName)}
              </div>
            );
          })}
        </div>
      </div>
    ));
  }

  return (
    <div className="bg-page dashboard adminSettings">
    <Topbar />
    <div className="pageName">Administrative Settings</div>
    <Container id="applicant-list-container" className="fullscreenWithTopBar top-0" fluid>
      <Row>
        <AdminNav />
        <div className="col-xl-12 col-xxl-10 px-0">
          <div className="px-4 py-3 py-xl-4 bg-white shadow-sm">
            <div className="d-flex align-items-center justify-content-between mx-xl-4">
              <h1 className="pageTitle text-nowrap text-primary tragedyRegularFont fw-700 mb-0 fs-3">Administrative Settings</h1>
              <button type='submit' className="link btn btn-sm btn-primary" disabled={settingsData.filter(setting => setting.updated).length === 0} onClick={submit}>Update</button>
              </div>
            </div>
            <AlertModal modal={showAlert} onClose={() => setAlert({"display":"toast","visible":false,"title":"","content":""})} />
            
            { !isLoaded ? <FullPageSpinner /> : (
            <>          
              <div className="px-4 pt-3 pt-lg-4 mx-xl-4 settingsArea">
                <Accordion className="row" flush defaultActiveKey={['0','1']} alwaysOpen>
                  <div className="col-md-6 col-lg-6 col-xl-6 pb-3">
                    <Accordion.Item eventKey="0" className="shadow">
                      <Accordion.Header>Global Settings</Accordion.Header>
                      <Accordion.Body className="px-3">
                      {renderSettingSection("global")}
                      </Accordion.Body>
                    </Accordion.Item>
                  </div>
                  <div className="col-md-6 col-lg-6 col-xl-6 pb-3">
                    <Accordion.Item eventKey="1" className="shadow">
                      <Accordion.Header>Integration Settings</Accordion.Header>
                      <Accordion.Body className="px-3">
                      {renderSettingSection("integration")}
                      </Accordion.Body>
                    </Accordion.Item>
                  </div>
                </Accordion>
              </div>
            </>
            ) }

          </div>
        </Row>
      </Container>
    </div>
  );
}